export const SlottedHostFields = {
  ccCard: "pnx-payment-from-nuvei-field-card",
  ccExp: "pnx-payment-from-nuvei-field-exp",
  ccCvc: "pnx-payment-from-nuvei-field-cvc",
} as const;

/**
 * Slot names for payment buttons. Same pattern as SlottedHostFields / NuveiNewCreditCardWidget:
 * insertHostFields creates light-DOM divs with these slot names; button components render
 * <slot name="..."> so that content is projected into the layout.
 */
export const PaymentButtonSlots = {
  applePay: "pnx-cashier-apple-pay-button",
  googlePay: "pnx-cashier-google-pay-button",
  paypal: "pnx-cashier-paypal-button",
} as const;

/** IDs of the slot container divs (in the host's light DOM when running inside a shadow root). */
export const PaymentButtonSlotIds = {
  applePay: "pnx-cashier-apple-pay-slot",
  googlePay: "pnx-cashier-google-pay-slot",
  paypal: "pnx-cashier-paypal-slot",
} as const;

/**
 * Set when the app mounts inside a shadow root (e.g. qiankun/MFE). Payment buttons use this
 * to render into the host's light-DOM container and create the custom element there (so it
 * attaches its shadow root). Cleared on unmount.
 */
let paymentButtonsInShadowDom = false;

export function setPaymentButtonsInShadowDom(value: boolean): void {
  paymentButtonsInShadowDom = value;
}

export function getPaymentButtonsInShadowDom(): boolean {
  return paymentButtonsInShadowDom;
}

/** Returns the host's light-DOM slot container for the given slot id, or null. */
export function getPaymentButtonSlotContainer(
  fromNode: Element,
  slotId: string
): HTMLElement | null {
  const root = fromNode.getRootNode();
  if (!(root instanceof ShadowRoot)) return null;
  const host = root.host;
  const el = Array.from(host.children).find((child) => child.id === slotId);
  return (el as HTMLElement) ?? null;
}

export const isInShadowDom = (node: Element) =>
  node.getRootNode() instanceof ShadowRoot;

function ensureSlot(
  shadowHost: Element,
  slotName: string,
  id: string
): HTMLElement {
  let el = shadowHost.querySelector(
    `[slot="${slotName}"]`
  ) as HTMLElement | null;
  if (!el) {
    el = document.createElement("div");
    el.setAttribute("id", id);
    el.setAttribute("slot", slotName);
    el.style.width = "100%";
    el.style.minHeight = "48px";
    shadowHost.appendChild(el);
  }
  return el;
}

export const insertHostFields = ({
  shadowHost,
}: {
  shadowHost: Element;
}): void => {
  if (!shadowHost.querySelectorAll("slot").length) {
    Object.values(SlottedHostFields).forEach((slotName) => {
      const slot = document.createElement("div");
      slot.setAttribute("id", slotName);
      slot.setAttribute("slot", slotName);
      slot.style.width = "100%";
      shadowHost.appendChild(slot);
    });
  }

  ensureSlot(
    shadowHost,
    PaymentButtonSlots.applePay,
    PaymentButtonSlotIds.applePay
  );
  ensureSlot(
    shadowHost,
    PaymentButtonSlots.googlePay,
    PaymentButtonSlotIds.googlePay
  );
  ensureSlot(
    shadowHost,
    PaymentButtonSlots.paypal,
    PaymentButtonSlotIds.paypal
  );
};
